import React, { useState, useEffect, useRef, useMemo } from 'react';
import { NavLink, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import WorkspaceSelectionModal from './auth/WorkspaceSelectionModal';
import ChangePasswordModal from './auth/ChangePasswordModal';
import EmployeeSecurityHub from './auth/EmployeeSecurityHub';
import ModuleErrorBoundary from './common/ModuleErrorBoundary';
import './Sidebar.css';
import {
  getAuthorizedNavigation,
  getAllAuthorizedLeafItems,
  getNavigationBreadcrumbs
} from '../config/navigationConfig';

/* ─── Inline SVG Icons ────────────────────────────────────────────────────── */

const Icon = ({ d, size = 18, strokeWidth = 2, className = '' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    style={{ flexShrink: 0 }}
  >
    {d}
  </svg>
);

const iconPaths = {
  dashboard:   <><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/></>,
  operations:  <><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/><path d="m9 14 2 2 4-4"/></>,
  queue:       <><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><circle cx="4" cy="6" r="1"/><circle cx="4" cy="12" r="1"/><circle cx="4" cy="18" r="1"/></>,
  calendar:    <><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>,
  registration:<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></>,
  phlebotomy:  <><path d="M4.5 9.5V5a2 2 0 0 1 4 0v4.5"/><line x1="6.5" y1="9.5" x2="6.5" y2="14"/><circle cx="6.5" cy="16.5" r="2.5"/><path d="M9 4H19M9 8h6"/></>,
  sample:      <><path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v11m0 0H5m4 0h5m0 0V3m0 8h5m0 0V3"/><circle cx="7" cy="18" r="1"/><circle cx="17" cy="18" r="1"/></>,
  outsource:   <><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/></>,
  pathology:   <><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></>,
  radiology:   <><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></>,
  microbiology:<><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3"/><circle cx="8" cy="10" r="1"/><circle cx="15" cy="14" r="1"/><circle cx="14" cy="9" r="1"/></>,
  approval:    <><circle cx="12" cy="8" r="6"/><path d="m9 8 2 2 4-4"/><path d="M8.21 13.89 7 23l5-3 5 3-1.21-9.12"/></>,
  chart:       <><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></>,
  settlement:  <><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></>,
  referral:    <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/><polyline points="19 8 22 11 19 14"/></>,
  masters:     <><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></>,
  testtube:    <><path d="M14.5 2v17.5c0 1.4-1.1 2.5-2.5 2.5h0c-1.4 0-2.5-1.1-2.5-2.5V2"/><path d="M8.5 2h7"/><path d="M14.5 16h-5"/></>,
  profiles:    <><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></>,
  package:     <><path d="m7.5 4.27 9 5.15"/><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/></>,
  observations:<><path d="M2 12h5l3 5 4-10 3 5h5"/></>,
  mapping:     <><circle cx="6" cy="6" r="3"/><circle cx="18" cy="18" r="3"/><path d="M6 9v3a6 6 0 0 0 6 6h3"/></>,
  specimens:   <><path d="M9 3h6v2H9z"/><path d="M10 5v14a2 2 0 0 0 4 0V5"/></>,
  departments: <><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><path d="M10 6.5h4M17.5 10v4M10 17.5h4M6.5 10v4"/></>,
  pricing:     <><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></>,
  panels:      <><rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/><line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/></>,
  party:       <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
  contracts:   <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></>,
  rates:       <><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></>,
  clinicians:  <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/><path d="M12 11v3"/><path d="M10.5 12.5h3"/></>,
  template:    <><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></>,
  signature:   <><path d="m18 2 4 4-10 10H8v-4L18 2z"/><path d="M2 22h20"/></>,
  building:    <><rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><line x1="8" y1="6" x2="8.01" y2="6"/><line x1="16" y1="6" x2="16.01" y2="6"/><line x1="12" y1="6" x2="12.01" y2="6"/><line x1="8" y1="10" x2="8.01" y2="10"/><line x1="16" y1="10" x2="16.01" y2="10"/><line x1="12" y1="10" x2="12.01" y2="10"/><line x1="8" y1="14" x2="8.01" y2="14"/><line x1="16" y1="14" x2="16.01" y2="14"/><line x1="12" y1="14" x2="12.01" y2="14"/></>,
  location:    <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></>,
  employees:   <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
  roles:       <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
  audit:       <><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></>,
  sliders:     <><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></>,
  award:       <><circle cx="12" cy="8" r="7"/><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/></>,
  star:        <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>,
  starFilled:  <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" fill="currentColor"/></>,
  clock:       <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
  search:      <><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></>,
  chevronLeft: <><polyline points="15 18 9 12 15 6"/></>,
  chevronRight:<><polyline points="9 18 15 12 9 6"/></>,
  chevronDown: <><polyline points="6 9 12 15 18 9"/></>,
  sun:         <><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></>,
  moon:        <><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></>,
  palette:     <><circle cx="13.5" cy="6.5" r=".7" fill="currentColor"/><circle cx="17.5" cy="10.5" r=".7" fill="currentColor"/><circle cx="8.5" cy="7.5" r=".7" fill="currentColor"/><circle cx="6.5" cy="12.5" r=".7" fill="currentColor"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.9 0 1.6-.7 1.6-1.6 0-.4-.2-.8-.5-1.1-.3-.3-.5-.7-.5-1.1 0-.9.7-1.6 1.6-1.6H16c3.3 0 6-2.7 6-6 0-5.5-4.5-9.6-10-9.6z"/></>,
  logout:      <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></>,
  menu:        <><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></>,
  x:           <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>,
  home:        <><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>,
  config:      <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></>,
  worksheet:   <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/></>,
  saas:        <><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/></>,
  book:        <><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></>,
  shield:      <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
  coins:       <><circle cx="8" cy="8" r="6"/><path d="M18.09 10.37A6 6 0 1 1 10.34 18"/><path d="M7 6h1v4"/><path d="m16.7 13.3.6.7"/></>,
  billing:     <><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></>,
  lab:         <><path d="M10 2v7.31L4.1 19.5a2 2 0 0 0 1.66 3h12.48a2 2 0 0 0 1.66-3L14 9.31V2"/></>,
};

const resolveIcon = (iconName) => iconPaths[iconName] || iconPaths.home;

/* ─── Main Layout Component ───────────────────────────────────────────────── */

export const Layout = () => {
  const { user, logout, switchWorkspace, can } = useAuth();
  const { theme, colorTheme, toggleTheme, setColorTheme, availableColorThemes } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const dropdownRef = useRef(null);
  const paletteRef = useRef(null);
  const searchInputRef = useRef(null);

  /* ── State ── */

  useEffect(() => {
    const handleToggleMenu = () => setMobileSidebarOpen((prev) => !prev);
    const handleOpenWorkspace = () => setWorkspaceModalOpen(true);
    const handleOpenSecurityHub = () => setSecurityHubOpen(true);
    window.addEventListener('toggle-lis-menu', handleToggleMenu);
    window.addEventListener('open-workspace-modal', handleOpenWorkspace);
    window.addEventListener('open-security-hub', handleOpenSecurityHub);
    return () => {
      window.removeEventListener('toggle-lis-menu', handleToggleMenu);
      window.removeEventListener('open-workspace-modal', handleOpenWorkspace);
      window.removeEventListener('open-security-hub', handleOpenSecurityHub);
    };
  }, []);

  const [workspaceModalOpen, setWorkspaceModalOpen] = useState(false);
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [securityHubOpen, setSecurityHubOpen] = useState(false);
  const [warningDismissed, setWarningDismissed] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('lis_sidebar_collapsed') === 'true'
  );
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [paletteDropdownOpen, setPaletteDropdownOpen] = useState(false);
  const [menuSearchQuery, setMenuSearchQuery] = useState('');

  // Auto-open change password modal if password change is required/forced
  useEffect(() => {
    if (user?.passwordExpiry?.requiresChange) {
      setChangePasswordModalOpen(true);
    }
  }, [user?.passwordExpiry?.requiresChange]);

  // Persisted expanded module IDs
  const [expandedModules, setExpandedModules] = useState(() => {
    try {
      const saved = localStorage.getItem('lis_expanded_modules');
      return saved ? JSON.parse(saved) : ['registration-billing', 'sample-management', 'result-entry', 'masters', 'security-admin'];
    } catch {
      return ['registration-billing', 'sample-management', 'result-entry', 'masters', 'security-admin'];
    }
  });

  // Persisted expanded Masters subgroups
  const [expandedMasterSubgroups, setExpandedMasterSubgroups] = useState(() => {
    try {
      const saved = localStorage.getItem('lis_expanded_master_subgroups');
      return saved ? JSON.parse(saved) : ['sub-tests'];
    } catch {
      return ['sub-tests'];
    }
  });

  /* ── Filtered Navigation Tree based on active user rights ── */
  const authorizedNavigation = useMemo(() => {
    return getAuthorizedNavigation(user, can);
  }, [user, can]);

  /* ── Flattened Authorized Leaves for Quick Jump / Search ── */
  const authorizedLeaves = useMemo(() => {
    return getAllAuthorizedLeafItems(authorizedNavigation);
  }, [authorizedNavigation]);

  /* ── Automatically expand active module on direct URL navigation ── */
  useEffect(() => {
    const curPath = location.pathname;
    if (curPath.startsWith('/registration') || curPath.startsWith('/settlement') || curPath.startsWith('/discount-approvals') || curPath.startsWith('/masters/referral-share')) {
      setExpandedModules((prev) => prev.includes('registration-billing') ? prev : [...prev, 'registration-billing']);
    } else if (curPath.startsWith('/phlebotomy') || curPath.startsWith('/sample-receive') || curPath.startsWith('/outsource')) {
      setExpandedModules((prev) => prev.includes('sample-management') ? prev : [...prev, 'sample-management']);
    } else if (curPath.startsWith('/pathology') || curPath.startsWith('/radiology') || curPath.startsWith('/microbiology') || curPath.startsWith('/reports/approval-queue')) {
      setExpandedModules((prev) => prev.includes('result-entry') ? prev : [...prev, 'result-entry']);
    } else if (curPath.startsWith('/masters') || curPath.startsWith('/investigation-observation-mapping')) {
      setExpandedModules((prev) => prev.includes('masters') ? prev : [...prev, 'masters']);
      if (curPath.includes('investigation') || curPath.includes('profile') || curPath.includes('package') || curPath.includes('observation') || curPath.includes('specimen') || curPath.includes('department')) {
        setExpandedMasterSubgroups((prev) => prev.includes('sub-tests') ? prev : [...prev, 'sub-tests']);
      } else if (curPath.includes('panel') || curPath.includes('party') || curPath.includes('contract') || curPath.includes('rates')) {
        setExpandedMasterSubgroups((prev) => prev.includes('sub-pricing') ? prev : [...prev, 'sub-pricing']);
      } else if (curPath.includes('clinician') || curPath.includes('reference-lab') || curPath.includes('radiology-template') || curPath.includes('doctor-signature')) {
        setExpandedMasterSubgroups((prev) => prev.includes('sub-clinical') ? prev : [...prev, 'sub-clinical']);
      } else if (curPath.includes('centre') || curPath.includes('location')) {
        setExpandedMasterSubgroups((prev) => prev.includes('sub-organization') ? prev : [...prev, 'sub-organization']);
      } else if (curPath.includes('microbiology')) {
        setExpandedMasterSubgroups((prev) => prev.includes('sub-microbiology') ? prev : [...prev, 'sub-microbiology']);
      } else if (curPath.includes('analyzer')) {
        setExpandedMasterSubgroups((prev) => prev.includes('sub-analyzers') ? prev : [...prev, 'sub-analyzers']);
      }
    } else if (curPath.startsWith('/employee') || curPath.startsWith('/roles') || curPath.startsWith('/audit') || curPath.startsWith('/registration-config') || curPath.startsWith('/nabl-config')) {
      setExpandedModules((prev) => prev.includes('security-admin') ? prev : [...prev, 'security-admin']);
    }
  }, [location.pathname]);

  /* ── Keyboard shortcut for search (Alt + K or Ctrl + /) ── */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.altKey && e.key.toLowerCase() === 'k') || (e.ctrlKey && e.key === '/')) {
        e.preventDefault();
        if (sidebarCollapsed) {
          setSidebarCollapsed(false);
          localStorage.setItem('lis_sidebar_collapsed', 'false');
        }
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [sidebarCollapsed]);

  /* ── Close mobile sidebar on navigation ── */
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  /* ── Close profile dropdown on outside click ── */
  useEffect(() => {
    const handleOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setProfileDropdownOpen(false);
      }
    };
    if (profileDropdownOpen) {
      document.addEventListener('mousedown', handleOutside);
    }
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [profileDropdownOpen]);

  /* ── Close palette dropdown on outside click ── */
  useEffect(() => {
    const handleOutsidePalette = (e) => {
      if (paletteRef.current && !paletteRef.current.contains(e.target)) {
        setPaletteDropdownOpen(false);
      }
    };
    if (paletteDropdownOpen) {
      document.addEventListener('mousedown', handleOutsidePalette);
    }
    return () => document.removeEventListener('mousedown', handleOutsidePalette);
  }, [paletteDropdownOpen]);

  /* ── Handlers ── */
  const handleLogout = async () => {
    setProfileDropdownOpen(false);
    await logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('lis_sidebar_collapsed', String(next));
      return next;
    });
  };

  const toggleModule = (moduleId) => {
    if (sidebarCollapsed) {
      setSidebarCollapsed(false);
      localStorage.setItem('lis_sidebar_collapsed', 'false');
    }
    setExpandedModules((prev) => {
      const exists = prev.includes(moduleId);
      const next = exists ? prev.filter((id) => id !== moduleId) : [...prev, moduleId];
      localStorage.setItem('lis_expanded_modules', JSON.stringify(next));
      return next;
    });
  };

  const toggleMasterSubgroup = (subgroupId) => {
    setExpandedMasterSubgroups((prev) => {
      const exists = prev.includes(subgroupId);
      const next = exists ? prev.filter((id) => id !== subgroupId) : [...prev, subgroupId];
      localStorage.setItem('lis_expanded_master_subgroups', JSON.stringify(next));
      return next;
    });
  };

  /* ── Search Filter Logic ── */
  const searchResults = useMemo(() => {
    const q = menuSearchQuery.trim().toLowerCase();
    if (!q) return null;
    return authorizedLeaves.filter((leaf) => {
      const titleMatch = leaf.title.toLowerCase().includes(q);
      const categoryMatch = (leaf.category || '').toLowerCase().includes(q);
      const keywordMatch = (leaf.keywords || []).some((kw) => kw.toLowerCase().includes(q));
      return titleMatch || categoryMatch || keywordMatch;
    });
  }, [menuSearchQuery, authorizedLeaves]);

  // Current Breadcrumbs based on location
  const breadcrumbs = useMemo(() => {
    return getNavigationBreadcrumbs(location.pathname);
  }, [location.pathname]);

  const userInitial = (user?.employeeName || user?.username || 'U').charAt(0).toUpperCase();
  const userName = user?.employeeName || user?.username || 'User';
  const userRole = user?.roleName || 'Staff';

  return (
    <div
      className={`app-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}
      style={{
        display: 'flex',
        minHeight: '100vh',
        '--current-sidebar-width': sidebarCollapsed ? '72px' : '272px'
      }}
    >
      {/* ─── Modern Left Navigation Sidebar ──────────────────────────── */}
      <aside
        className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileSidebarOpen ? 'active-mobile mobile-open' : ''}`}
        aria-label="inTeLiSYS Main Navigation"
      >
        {/* Sidebar Header & Brand */}
        <div className="sidebar-header">
          {!sidebarCollapsed ? (
            <div className="sidebar-brand" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
              <div className="sidebar-logo-icon" aria-hidden="true">iT</div>
              <div className="sidebar-brand-text">
                <span className="sidebar-brand-title" aria-label="inTeLiSYS">
                  <span className="bt-lo">in</span><span className="bt-hi">TeLi</span><span className="bt-ac">SYS</span>
                </span>
                <span className="sidebar-brand-sub">Clinical Diagnostics</span>
              </div>
            </div>
          ) : (
            <div
              className="sidebar-logo-icon"
              title="inTeLiSYS - Click to expand"
              onClick={toggleSidebar}
              style={{ cursor: 'pointer' }}
            >
              iT
            </div>
          )}

          {/* Desktop sidebar toggle button */}
          <button
            className="sidebar-toggle-btn desktop-toggle-btn"
            onClick={toggleSidebar}
            title={sidebarCollapsed ? 'Expand sidebar (Alt + [)' : 'Collapse sidebar (Alt + [)'}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            id="sidebar-collapse-toggle-btn"
          >
            <Icon d={sidebarCollapsed ? iconPaths.chevronRight : iconPaths.chevronLeft} size={16} strokeWidth={2.5} />
          </button>
        </div>

        {/* Sidebar Search Filter Box */}
        <div className="sidebar-search-box">
          <div className="sidebar-search-wrapper">
            <span className="sidebar-search-icon">
              <Icon d={iconPaths.search} size={14} />
            </span>
            <input
              ref={searchInputRef}
              type="text"
              className="sidebar-search-input"
              placeholder="Search screens"
              value={menuSearchQuery}
              onChange={(e) => setMenuSearchQuery(e.target.value)}
              title="Filter navigation menus by title or keyword"
            />
            {!menuSearchQuery && <kbd className="sidebar-search-kbd" aria-hidden="true">Alt K</kbd>}
            {menuSearchQuery && (
              <button
                type="button"
                className="sidebar-search-clear-btn"
                onClick={() => setMenuSearchQuery('')}
                title="Clear search"
                style={{
                  position: 'absolute',
                  right: '0.6rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: '2px',
                  display: 'flex'
                }}
              >
                <Icon d={iconPaths.x} size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Sidebar Navigation Items */}
        <nav className="sidebar-nav lis-modern-nav" aria-label="Application Navigation Hierarchy">
          {/* SEARCH RESULTS MODE */}
          {searchResults !== null ? (
            <div className="sidebar-search-results-pane">
              <div className="sidebar-section-title" style={{ padding: '0.25rem 0.5rem', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)' }}>
                Matching Screens ({searchResults.length})
              </div>
              {searchResults.length === 0 ? (
                <div className="sidebar-no-results" style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                  No authorized screens match "{menuSearchQuery}"
                </div>
              ) : (
                searchResults.map((leaf) => (
                  <NavLink
                    key={leaf.path}
                    to={leaf.path}
                    className={({ isActive }) => `menu-flat-btn menu-item-btn ${isActive ? 'active' : ''}`}
                    onClick={() => {
                      setMobileSidebarOpen(false);
                      setMenuSearchQuery('');
                    }}
                  >
                    <span className="menu-item-icon">
                      <Icon d={resolveIcon(leaf.icon)} size={18} strokeWidth={2} />
                    </span>
                    <div className="menu-search-result-content" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1 }}>
                      <span className="menu-item-label" style={{ fontWeight: 600 }}>{leaf.title}</span>
                      <span className="menu-search-category-pill" style={{ fontSize: '0.7rem', color: 'var(--text-muted)', opacity: 0.85 }}>
                        {leaf.category}
                      </span>
                    </div>
                  </NavLink>
                ))
              )}
            </div>
          ) : (
            /* STANDARD HIERARCHICAL NAVIGATION MODE */
            <>
              {/* 1. Dashboard (Direct Top Link) */}
              <NavLink
                to="/dashboard"
                className={({ isActive }) => `menu-flat-btn menu-item-btn ${isActive ? 'active' : ''}`}
                title={sidebarCollapsed ? 'Dashboard' : undefined}
                onClick={() => setMobileSidebarOpen(false)}
                id="nav-dashboard-link"
              >
                <span className="menu-item-icon">
                  <Icon d={iconPaths.dashboard} size={18} strokeWidth={2} />
                </span>
                <span className="menu-item-label">Dashboard</span>
              </NavLink>

              {/* 2. Operational Navigation Modules */}
              {authorizedNavigation.map((mod) => {
                if (mod.type === 'single') {
                  return null; // Handled directly as Dashboard
                }

                const isModuleExpanded = expandedModules.includes(mod.id);
                const hasActiveChild = mod.type === 'group'
                  ? (mod.items || []).some((item) => location.pathname === item.path || location.pathname.startsWith(item.path + '/'))
                  : mod.hubPath === location.pathname || (mod.subgroups || []).some((sub) => (sub.items || []).some((i) => location.pathname === i.path || location.pathname.startsWith(i.path + '/')));

                return (
                  <div
                    key={mod.id}
                    className={`sidebar-module-section ${hasActiveChild ? 'has-active-child' : ''} ${isModuleExpanded ? 'expanded' : 'collapsed'}`}
                  >
                    {/* Module Accordion Header */}
                    <button
                      type="button"
                      className={`sidebar-module-header ${hasActiveChild ? 'module-active' : ''}`}
                      onClick={() => toggleModule(mod.id)}
                      title={sidebarCollapsed ? mod.title : undefined}
                      aria-expanded={isModuleExpanded}
                    >
                      <div className="sidebar-module-header-left">
                        <span className="module-header-icon">
                          <Icon d={resolveIcon(mod.icon)} size={17} strokeWidth={2.2} />
                        </span>
                        {!sidebarCollapsed && (
                          <span className="module-header-title">{mod.title}</span>
                        )}
                      </div>
                      {!sidebarCollapsed && (
                        <div className="sidebar-module-header-right">
                          <span className="sidebar-count-badge">
                            {mod.type === 'group' ? mod.items?.length : mod.subgroups?.reduce((acc, s) => acc + s.items.length, 0)}
                          </span>
                          <span className={`module-chevron ${isModuleExpanded ? 'rotated' : ''}`}>
                            <Icon d={iconPaths.chevronDown} size={14} strokeWidth={2.5} />
                          </span>
                        </div>
                      )}
                    </button>

                    {/* Module Submenu Content */}
                    {!sidebarCollapsed && isModuleExpanded && (
                      <div className="sidebar-module-body fade-in">
                        {mod.type === 'group' && (
                          <div className="sidebar-module-items-list">
                            {(mod.items || []).map((item) => {
                              const active = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                              return (
                                <NavLink
                                  key={item.path}
                                  to={item.path}
                                  className={`menu-child-btn ${active ? 'active' : ''}`}
                                  onClick={() => setMobileSidebarOpen(false)}
                                  title={item.title}
                                >
                                  <span className="menu-item-icon">
                                    <Icon d={resolveIcon(item.icon)} size={16} strokeWidth={2} />
                                  </span>
                                  <span className="menu-item-label">{item.title}</span>
                                </NavLink>
                              );
                            })}
                          </div>
                        )}

                        {/* MASTERS DIRECTORY (Nested Groups + Hub Link) */}
                        {mod.type === 'nested_group' && (
                          <div className="sidebar-masters-container">
                            {/* All Masters Hub Button */}
                            <NavLink
                              to={mod.hubPath}
                              className={({ isActive }) => `masters-hub-link ${isActive ? 'active' : ''}`}
                              onClick={() => setMobileSidebarOpen(false)}
                              title="Open All Masters Visual Directory"
                            >
                              <span className="menu-item-icon">
                                <Icon d={iconPaths.masters} size={16} strokeWidth={2} />
                              </span>
                              <span className="menu-item-label" style={{ fontWeight: 600 }}>All Masters Directory</span>
                              <span className="sidebar-item-micro-badge" style={{ background: 'var(--accent-light)', color: 'var(--accent)' }}>Hub</span>
                            </NavLink>

                            {/* Master Subgroups */}
                            {(mod.subgroups || []).map((sub) => {
                              const isSubExpanded = expandedMasterSubgroups.includes(sub.id);
                              const hasActiveSubItem = sub.items.some((i) => location.pathname === i.path || location.pathname.startsWith(i.path + '/'));
                              return (
                                <div key={sub.id} className={`sidebar-subgroup ${isSubExpanded ? 'sub-expanded' : ''}`}>
                                  <button
                                    type="button"
                                    className={`sidebar-subgroup-header ${hasActiveSubItem ? 'subgroup-active' : ''}`}
                                    onClick={() => toggleMasterSubgroup(sub.id)}
                                    title={sub.title}
                                  >
                                    <div className="subgroup-header-left">
                                      <span className="subgroup-icon">
                                        <Icon d={resolveIcon(sub.icon)} size={14} strokeWidth={2} />
                                      </span>
                                      <span className="subgroup-title">{sub.title}</span>
                                    </div>
                                    <span className={`subgroup-chevron ${isSubExpanded ? 'rotated' : ''}`}>
                                      <Icon d={iconPaths.chevronRight} size={12} strokeWidth={2.5} />
                                    </span>
                                  </button>

                                  {isSubExpanded && (
                                    <div className="sidebar-subgroup-items">
                                      {sub.items.map((item) => {
                                        const active = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                                        return (
                                          <NavLink
                                            key={item.path}
                                            to={item.path}
                                            className={`menu-subchild-btn ${active ? 'active' : ''}`}
                                            onClick={() => setMobileSidebarOpen(false)}
                                            title={item.title}
                                          >
                                            <span className="subchild-dot">•</span>
                                            <span className="menu-item-label">{item.title}</span>
                                          </NavLink>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </nav>

        {/* Sidebar Footer — User Profile & Role Status */}
        <div className="sidebar-footer" ref={dropdownRef}>
          {profileDropdownOpen && (
            <div className="profile-dropdown-overlay" onClick={() => setProfileDropdownOpen(false)} />
          )}

          <div className={`sidebar-profile-container ${profileDropdownOpen ? 'dropdown-active' : ''}`}>
            <button
              className="sidebar-profile-toggle"
              onClick={() => setProfileDropdownOpen((prev) => !prev)}
              title={sidebarCollapsed ? `${userName} (${userRole})` : 'User Profile Options'}
              aria-label="User profile menu"
              aria-expanded={profileDropdownOpen}
              id="sidebar-profile-btn"
            >
              <div className="sidebar-user-avatar">{userInitial}</div>
              <div className="sidebar-user-meta">
                <span className="sidebar-user-name">{userName}</span>
                <span className="sidebar-user-role">{userRole}</span>
              </div>
              <span className="profile-chevron">
                <Icon d={iconPaths.chevronDown} size={14} strokeWidth={2.5} />
              </span>
            </button>

            {profileDropdownOpen && (
              <div className="sidebar-profile-dropdown fade-in">
                <div className="dropdown-header">
                  <div className="dropdown-user-avatar">{userInitial}</div>
                  <div className="dropdown-user-info">
                    <span className="dropdown-user-name">{userName}</span>
                    <span className="dropdown-user-role">{userRole}</span>
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                      Center: {user?.centreName || 'Main Lab'}
                    </span>
                  </div>
                </div>

                <div className="dropdown-divider" />

                <div className="dropdown-body">
                  <button
                    type="button"
                    className="dropdown-item"
                    onClick={() => {
                      setProfileDropdownOpen(false);
                      setSecurityHubOpen(true);
                    }}
                    id="sidebar-security-hub-btn"
                  >
                    <span>🛡️</span>
                    <span>Security & Sessions</span>
                  </button>
                  <button
                    type="button"
                    className="dropdown-item"
                    onClick={() => {
                      setProfileDropdownOpen(false);
                      setChangePasswordModalOpen(true);
                    }}
                    id="sidebar-change-password-btn"
                  >
                    <span>🔐</span>
                    <span>Change Password</span>
                  </button>
                  <button
                    type="button"
                    className="dropdown-item"
                    onClick={() => {
                      setProfileDropdownOpen(false);
                      setWorkspaceModalOpen(true);
                    }}
                  >
                    <span>🔄</span>
                    <span>Switch Operating Center</span>
                  </button>
                  <button
                    type="button"
                    className="dropdown-item logout-item"
                    onClick={handleLogout}
                    id="sidebar-logout-btn"
                  >
                    <Icon d={iconPaths.logout} size={16} strokeWidth={2} />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* ─── Mobile Overlay ──────────────────────────── */}
      {mobileSidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setMobileSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ─── Main Content Canvas ────────────────────────────────────── */}
      <div className="main-area" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Topbar */}
        <header className="topbar" role="banner">
          {/* Left side: mobile toggle + center indicator */}
          <div className="topbar-left">
            <button
              className="icon-button mobile-menu-toggle"
              onClick={() => setMobileSidebarOpen((prev) => !prev)}
              aria-label="Toggle navigation menu"
              id="mobile-menu-toggle-btn"
            >
              <Icon d={mobileSidebarOpen ? iconPaths.x : iconPaths.menu} size={20} strokeWidth={2.2} />
            </button>

            {/* Centre badge & Switch Workspace Button */}
            <div className="lis-header-centre" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>🏥</span>
              <div className="lis-centre-info">
                <span className="lis-centre-name">{user?.centreName || 'Main Lab'}</span>
                <span className="lis-centre-code">Code: {user?.centreCode || 'AUR'}</span>
              </div>
              <button
                type="button"
                onClick={() => setWorkspaceModalOpen(true)}
                className="workspace-switch-badge-btn"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  padding: '0.25rem 0.65rem',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  borderRadius: '0.375rem',
                  border: '1px solid var(--border-color, #cbd5e1)',
                  background: 'var(--bg-surface, #f8fafc)',
                  color: 'var(--text-primary, #1e293b)',
                  cursor: 'pointer',
                  marginLeft: '0.25rem',
                  transition: 'all 0.15s'
                }}
                title="Switch Operating Center or Clinical Role"
              >
                <span>🔄</span> Switch
              </button>

              {user?.tenantCode && user?.tenantCode !== 'PRIMARY' && (
                <span
                  className="tenant-pill-badge"
                  style={{
                    padding: '0.2rem 0.65rem',
                    borderRadius: '1rem',
                    fontSize: '0.72rem',
                    fontWeight: '700',
                    background: '#e0f2fe',
                    color: '#0369a1',
                    border: '1px solid #7dd3fc',
                    letterSpacing: '0.04em',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                  title={`Active Tenant: ${user.tenantCode} (ID: ${user.tenantId})`}
                >
                  <span style={{ fontSize: '0.65rem' }}>🏢</span>
                  TENANT: {user.tenantCode}
                </span>
              )}
            </div>
          </div>

          {/* Right side: theme controls + user + sign out */}
          <div className="topbar-right">
            <div className="theme-controls-group" ref={paletteRef}>
              {/* Dark/Light toggle */}
              <button
                type="button"
                className="theme-toggle-btn"
                onClick={toggleTheme}
                title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
                aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
                id="theme-toggle-btn"
              >
                <Icon d={theme === 'light' ? iconPaths.moon : iconPaths.sun} size={18} strokeWidth={2} />
              </button>

              {/* Color Palette button */}
              <button
                type="button"
                className={`theme-palette-btn ${paletteDropdownOpen ? 'active' : ''}`}
                onClick={() => setPaletteDropdownOpen((prev) => !prev)}
                title="Change Theme Color Palette"
                aria-label="Change Theme Color Palette"
                id="theme-palette-btn"
              >
                <Icon d={iconPaths.palette} size={18} strokeWidth={2} />
              </button>

              {/* Palette Dropdown */}
              {paletteDropdownOpen && (
                <div className="theme-palette-dropdown">
                  <div className="theme-palette-header">
                    <div>
                      <div className="theme-palette-title">Color Themes</div>
                      <div className="theme-palette-subtitle">Customize application accent</div>
                    </div>
                  </div>
                  <div className="theme-palette-grid">
                    {availableColorThemes.map((ct) => (
                      <button
                        key={ct.id}
                        type="button"
                        className={`color-theme-card ${colorTheme === ct.id ? 'active' : ''}`}
                        onClick={() => {
                          setColorTheme(ct.id);
                          setPaletteDropdownOpen(false);
                        }}
                      >
                        <span className="color-swatch-circle" style={{ backgroundColor: ct.color }}>
                          {colorTheme === ct.id && (
                            <span style={{ color: '#fff', fontSize: '10px', fontWeight: 800 }}>✓</span>
                          )}
                        </span>
                        <span className="color-theme-label">{ct.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Phase 6E: Global Critical Panic Alert Notification Banner */}

        {/* Password Expiry Warning / Required Alert Banner */}
        {user?.passwordExpiry?.requiresChange ? (
          <div
            className="pwd-expiry-banner pwd-expiry-banner-danger"
            style={{
              background: '#fef2f2',
              borderBottom: '1px solid #fecaca',
              color: '#991b1b',
              padding: '0.65rem 1.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '0.85rem',
              fontWeight: '600',
              zIndex: 90
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <span style={{ fontSize: '1.1rem' }}>🚨</span>
              <span>{user.passwordExpiry.statusMessage || 'Your password has expired. Please change your password to continue.'}</span>
            </div>
            <button
              type="button"
              onClick={() => setChangePasswordModalOpen(true)}
              style={{
                background: '#dc2626',
                color: '#fff',
                border: 'none',
                padding: '0.35rem 0.85rem',
                borderRadius: '0.375rem',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '0.8rem'
              }}
            >
              Change Password Now
            </button>
          </div>
        ) : user?.passwordExpiry?.isWarning && !warningDismissed ? (
          <div
            className="pwd-expiry-banner pwd-expiry-banner-warning"
            style={{
              background: '#fffbeb',
              borderBottom: '1px solid #fef3c7',
              color: '#92400e',
              padding: '0.65rem 1.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '0.85rem',
              fontWeight: '600',
              zIndex: 90
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <span style={{ fontSize: '1.1rem' }}>⚠️</span>
              <span>{user.passwordExpiry.statusMessage || `Your password will expire in ${user.passwordExpiry.daysRemaining} days. Please change your password.`}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                type="button"
                onClick={() => setChangePasswordModalOpen(true)}
                style={{
                  background: '#d97706',
                  color: '#fff',
                  border: 'none',
                  padding: '0.35rem 0.85rem',
                  borderRadius: '0.375rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '0.8rem'
                }}
              >
                Change Password
              </button>
              <button
                type="button"
                onClick={() => setWarningDismissed(true)}
                style={{
                  background: 'transparent',
                  color: '#92400e',
                  border: '1px solid #fcd34d',
                  padding: '0.35rem 0.65rem',
                  borderRadius: '0.375rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '0.8rem'
                }}
                title="Dismiss warning for this session"
              >
                Dismiss
              </button>
            </div>
          </div>
        ) : null}

        {/* Global Breadcrumb Navigation Bar (Omitted on Registration page to allow single unified workbench header) */}
        {location.pathname !== '/registration' && (
          <div className="global-breadcrumb-bar" role="navigation" aria-label="Breadcrumb">
            <div className="breadcrumb-trail">
              {breadcrumbs.map((crumb, idx) => (
                <React.Fragment key={crumb.label + idx}>
                  {idx > 0 && <span className="breadcrumb-separator">›</span>}
                  {crumb.path && !crumb.isCurrent ? (
                    <NavLink to={crumb.path} className="breadcrumb-link">
                      {idx === 0 && <span style={{ marginRight: '4px', fontSize: '0.85rem' }}>🏠</span>}
                      {crumb.label}
                    </NavLink>
                  ) : (
                    <span className={`breadcrumb-text ${crumb.isCurrent ? 'current' : ''}`}>
                      {idx === 0 && <span style={{ marginRight: '4px', fontSize: '0.85rem' }}>🏠</span>}
                      {crumb.label}
                    </span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Dynamic Page Router Outlet */}
        <main className="page-wrapper" role="main" id="main-content">
          <ModuleErrorBoundary resetKey={location.pathname}>
            <Outlet />
          </ModuleErrorBoundary>
        </main>

        {/* Copyright line: shown at the bottom of every signed-in screen */}
        <footer className="app-footer" role="contentinfo">
          <span>&copy; {new Date().getFullYear()} <strong>inTeLiSYS</strong>. All rights reserved.</span>
          <span className="app-footer-sep" aria-hidden="true">&middot;</span>
          <span>Clinical Diagnostics Suite</span>
        </footer>
      </div>

      {/* Runtime Workspace Switcher Modal */}
      <WorkspaceSelectionModal
        isOpen={workspaceModalOpen}
        workspaces={user?.availableWorkspaces || []}
        currentCentreId={user?.centreId}
        currentRoleId={user?.roleId}
        onSelect={async (centreId, roleId) => {
          const res = await switchWorkspace(centreId, roleId);
          if (res.success) {
            setWorkspaceModalOpen(false);
          } else {
            throw new Error(res.message);
          }
        }}
        onClose={() => setWorkspaceModalOpen(false)}
        title="Switch Operating Workspace"
        subtitle="Select an authorized diagnostic Center and Role to switch your working session"
      />

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={changePasswordModalOpen}
        isForced={!!user?.passwordExpiry?.requiresChange}
        forcedReason={user?.passwordExpiry?.statusMessage}
        onClose={() => setChangePasswordModalOpen(false)}
        onLogout={handleLogout}
        onSuccess={(updatedExpiry) => {
          setChangePasswordModalOpen(false);
          if (user && updatedExpiry) {
            const updatedUser = { ...user, passwordExpiry: updatedExpiry };
            localStorage.setItem('lis_user', JSON.stringify(updatedUser));
          }
        }}
      />

      {/* Employee Security Hub Modal */}
      <EmployeeSecurityHub
        isOpen={securityHubOpen}
        onClose={() => setSecurityHubOpen(false)}
        user={user}
        onOpenChangePassword={() => {
          setSecurityHubOpen(false);
          setChangePasswordModalOpen(true);
        }}
      />
    </div>
  );
};

export default Layout;

